import { dayWords } from "./billingPeriods.js";

const clean = (value) => String(value ?? "").trim();
const normalized = (value) => clean(value).toLowerCase();

const STATUS = {
  blocked: { label: "Bloqué", tone: "danger" },
  draft: { label: "Brouillon", tone: "warning" },
  eligible: { label: "Prêt", tone: "success" },
  failed: { label: "Erreur", tone: "danger" },
  issued: { label: "Numéroté", tone: "neutral" },
  overdue: { label: "En retard", tone: "danger" },
  paid: { label: "Payée", tone: "success" },
  pending: { label: "En attente", tone: "warning" },
  processing: { label: "En traitement", tone: "warning" },
  revalidation: { label: "Envoi bloqué", tone: "danger" },
  ready_to_send: { label: "Prête à envoyer", tone: "neutral" },
  sent: { label: "Envoyé", tone: "success" },
  stale: { label: "Obsolète", tone: "danger" },
  void: { label: "Annulé", tone: "muted" },
};

// Motifs que produisent les prestations de la Consolidation (seules lues par « À facturer »).
const BLOCKING_REASONS = {
  billing_account_missing: "Le client doit être associé à une entreprise facturée.",
  billing_mapping_required: "Cette entreprise facturée doit être rattachée à une entreprise de facturation.",
  consolidation_reopened: "La consolidation a été rouverte et doit être revalidée.",
  consolidation_stale: "Révision trop ancienne : rouvrez puis validez la source.",
  pm_delivery_pending: "Chargé PM en attente de la validation de sa livraison.",
  pm_source_review_required: "Chargé PM non livré : rouvrez et revalidez son relevé de chargement.",
  rate_missing: "Le tarif de ce volet n’est pas configuré.",
};

// dompeur_rate_missing : « rate_missing » d'une prestation de camion dompeur, déduit à l'affichage.
const BLOCKING_REASON_SHORT_LABELS = {
  billing_account_missing: "Sans entreprise",
  billing_mapping_required: "Sans entreprise",
  consolidation_reopened: "Consolidation à revalider",
  consolidation_stale: "Relevé à revalider",
  dompeur_rate_missing: "Tarif à la tonne manquant",
  pm_delivery_pending: "PM en attente de livraison",
  pm_source_review_required: "Relevé PM à revoir",
  rate_missing: "Tarif manquant",
};

/** Historical preference key retained for Consolidation services requiring completion. */
export const APPROVED_BLOCKED_FILTER_CODE = "__approved__";
export const APPROVED_BLOCKED_FILTER_LABEL = "À compléter";

export function billableBlockingReasonShortLabel(code) {
  const key = clean(code);
  if (!key) return "";
  if (key === APPROVED_BLOCKED_FILTER_CODE) return APPROVED_BLOCKED_FILTER_LABEL;
  return BLOCKING_REASON_SHORT_LABELS[key] || humanizeBillingValue(key);
}

export function billableBlockingReasonOptions(counts = []) {
  const source = Array.isArray(counts) ? counts : [];
  return source
    .map((entry) => {
      const code = clean(entry?.code);
      const count = Number(entry?.count) || 0;
      const label = billableBlockingReasonShortLabel(code);
      if (!code || !label || count <= 0 || code === APPROVED_BLOCKED_FILTER_CODE) return null;
      return { code, count, label };
    })
    .filter(Boolean);
}

export function humanizeBillingValue(value) {
  const text = clean(value).replaceAll("_", " ");
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "—";
}

export function billingStatusMeta(status) {
  const key = normalized(status);
  return STATUS[key] || { label: humanizeBillingValue(status), tone: "neutral" };
}

export function billingSideLabel(side) {
  const key = normalized(side);
  if (key === "loading") return "Chargement";
  if (key === "unloading") return "Déchargement";
  return humanizeBillingValue(side);
}

export function billingSideAbbrev(side) {
  const key = normalized(side);
  if (key === "loading") return "Ch.";
  if (key === "unloading") return "Dch.";
  return billingSideLabel(side);
}

export function invoiceOrderReferenceLabel(value) {
  const text = clean(value);
  if (!text) return "";
  const [primary] = text.split(" / ");
  return clean(primary) || text;
}

export const MANUAL_INVOICE_LINE_REFERENCE = "Ajustement";

export function isManualInvoiceLine(item) {
  if (item?.is_manual === true) return true;
  const snapshot = item?.snapshot_json && typeof item.snapshot_json === "object" ? item.snapshot_json : {};
  if (clean(snapshot.kind) === "manual_adjustment") return true;
  if (clean(item?.billable_item_id)) return false;
  return invoiceOrderReferenceLabel(item?.order_reference) === MANUAL_INVOICE_LINE_REFERENCE;
}

export function billableOrderReferenceLabel(item) {
  return invoiceOrderReferenceLabel(
    item?.order_number
      || item?.order_reference
      || item?.business_order_id
      || item?.source_order_id
      || item?.work_order_number,
  );
}

export function invoiceLineServiceLabel(description) {
  const text = clean(description);
  if (!text) return "";
  const calculated = text.match(/^(?:Chargement|Déchargement|Trajet et livraison|Inspection|Retour|Préchargement|Supplément à la tonne|Supplément voyage)\s+·\s+(.+?)(?:\s+·\s+|$)/i);
  if (calculated) return calculated[1].trim();
  return text.replace(/^(Chargement|Déchargement)\s+[—–-]\s+/i, "").trim() || text;
}

export function billingUnitLabel(unit, quantity) {
  const key = normalized(unit);
  if (key === "heure") return Number(quantity) === 1 ? "heure" : "heures";
  if (["ton", "tons", "tonne", "tonnes"].includes(key)) return Number(quantity) === 1 ? "tonne" : "tonnes";
  if (["trip", "trips", "voyage", "voyages"].includes(key)) return Number(quantity) === 1 ? "voyage" : "voyages";
  return humanizeBillingValue(unit);
}

/**
 * Regroupe les lignes tonne + voyage d'un même service en une rangée d'affichage.
 * Miroir de app.services.billing_invoice_line_presentation.group_invoice_lines_for_display.
 */
export function groupInvoiceLinesForDisplay(lines = [], displayLines = null) {
  const source = Array.isArray(lines) ? lines : [];
  const groups = new Map();
  const order = [];

  const groupKey = (line) => {
    if (isManualInvoiceLine(line)) return `manual:${clean(line?.id || line?.invoice_line_id)}`;
    const billableItemId = clean(line?.billable_item_id);
    if (billableItemId) return `item:${billableItemId}`;
    return [
      clean(line?.order_reference || line?.order_number),
      clean(line?.service_date),
      clean(line?.description),
    ].join("|");
  };

  source.forEach((line, index) => {
    if (!line || typeof line !== "object") return;
    const key = groupKey(line);
    const unit = normalized(line.billing_unit);
    const sortOrder = Number(line.sort_order) || index + 1;
    const lineTotal = Number(line.line_total) || 0;
		const originalLineTotal = Number(line.original_line_total ?? line.line_total) || 0;

    if (!groups.has(key)) {
      order.push(key);
      groups.set(key, {
        id: clean(line.id || line.invoice_line_id) || null,
        billable_item_id: clean(line.billable_item_id) || null,
        is_manual: isManualInvoiceLine(line),
        service_date: line.service_date,
        order_reference: invoiceOrderReferenceLabel(line.order_reference || line.order_number),
        description: clean(line.description),
        billing_unit: null,
        quantity: null,
        unit_rate: null,
        voyage_amount: null,
				original_quantity: null,
				original_unit_rate: null,
				original_voyage_amount: null,
				original_line_total: 0,
				is_modified: false,
        line_total: 0,
        sort_order: sortOrder,
        calculation_components: [],
      });
    }

    const group = groups.get(key);
    group.sort_order = Math.min(group.sort_order, sortOrder);
    group.line_total += lineTotal;
    group.calculation_components.push({
      invoice_line_id: line.id, kind: group.is_manual ? "manual_adjustment" : line.presentation_context?.kind,
      description: line.description, billing_unit: group.is_manual ? null : line.billing_unit,
      quantity: group.is_manual ? null : line.quantity, unit_rate: group.is_manual ? null : line.unit_rate,
      line_total: line.line_total, calculation_details: line.calculation_details, service_context: line.service_context,
    });
		group.original_line_total += originalLineTotal;
		group.is_modified = group.is_modified
			|| Number(line.quantity) !== Number(line.original_quantity ?? line.quantity)
			|| Number(line.unit_rate) !== Number(line.original_unit_rate ?? line.unit_rate)
			|| lineTotal !== originalLineTotal;

    if (["tonne", "heure"].includes(unit) && !group.is_manual) {
      group.billing_unit = unit;
      group.quantity = line.quantity;
      group.unit_rate = line.unit_rate;
			group.original_quantity = line.original_quantity ?? line.quantity;
			group.original_unit_rate = line.original_unit_rate ?? line.unit_rate;
      if (!group.service_date) group.service_date = line.service_date;
      if (!group.order_reference) group.order_reference = invoiceOrderReferenceLabel(line.order_reference || line.order_number);
      if (!group.description) group.description = clean(line.description);
    } else if (unit === "voyage" && !group.is_manual) {
      group.voyage_amount = (group.voyage_amount == null ? 0 : Number(group.voyage_amount)) + lineTotal;
			group.original_voyage_amount = (group.original_voyage_amount == null ? 0 : Number(group.original_voyage_amount)) + originalLineTotal;
      if (!group.service_date) group.service_date = line.service_date;
      if (!group.order_reference) group.order_reference = invoiceOrderReferenceLabel(line.order_reference || line.order_number);
      if (!group.description) group.description = clean(line.description);
    } else if (group.is_manual) {
      if (!group.description) group.description = clean(line.description);
      if (!group.order_reference) group.order_reference = invoiceOrderReferenceLabel(line.order_reference || line.order_number);
    }
  });

  const grouped = order.map((key) => {
    const group = groups.get(key);
    return {
      id: group.id,
      billable_item_id: group.billable_item_id,
      is_manual: Boolean(group.is_manual),
      service_date: group.service_date,
      order_reference: group.order_reference || null,
      description: group.description,
      billing_unit: group.billing_unit,
      quantity: group.quantity,
      unit_rate: group.unit_rate,
      voyage_amount: group.voyage_amount == null ? null : group.voyage_amount,
			original_quantity: group.original_quantity,
			original_unit_rate: group.original_unit_rate,
			original_voyage_amount: group.original_voyage_amount == null ? null : group.original_voyage_amount,
			original_line_total: group.original_line_total,
			is_modified: group.is_modified,
      line_total: group.line_total,
      sort_order: group.sort_order,
      calculation_details: { components: group.calculation_components },
    };
  });
  if (!Array.isArray(displayLines)) return grouped.sort((a, b) => Number(a.is_manual) - Number(b.is_manual));
  // La ventilation des prestations calculées est commune à l'API et au PDF.
  // Les ajustements libres restent éditables à partir de leurs valeurs locales.
  const byId = new Map(grouped.map((line) => [line.billable_item_id || line.id, line]));
  return displayLines.flatMap((line) => {
    const current = byId.get(line.billable_item_id || line.id);
    return current ? [current.is_manual ? current : {
      ...current, ...line,
      calculation_details: line.calculation_details || (line.return_details ? null : current.calculation_details),
    }] : [];
  });
}

export function invoiceTonneRate(line) {
  if (isManualInvoiceLine(line)) return null;
  const rate = line?.tonne_rate ?? (line?.billing_unit === "tonne" ? line.unit_rate : null);
  return rate == null || rate === "" || !Number.isFinite(Number(rate)) ? null : Number(rate);
}

export function invoiceTimeLabel(line) {
  const value = line?.time_hours ?? (line?.billing_unit === "heure" ? line.quantity : null);
  if (value == null || value === "" || !Number.isFinite(Number(value)) || Number(value) < 0) return "—";
  const minutes = Math.round(Number(value) * 60);
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return hours ? `${hours} h${remainder ? ` ${remainder} min` : ""}` : `${minutes} min`;
}

export function billingAccountLabel(account) {
  return clean(account?.name || account?.legal_name || account?.display_name) || "Entreprise sans nom";
}

export function billableSourceLabel(item) {
  const source = item?.source_client || item?.source_client_snapshot || {};
  return clean(
    item?.source_client_name
      || source.source_name
      || source.name
      || source.client_name
      || source.correspondence_name
      || item?.client_name,
  ) || "Client source introuvable";
}

export function billablePreviewItemLabel(item) {
  const orderNumber = billableOrderReferenceLabel(item);
  const sourceName = clean(
    item?.source_client_name
      || item?.source_client?.client_name
      || item?.source_client?.correspondence_name
      || item?.source_client?.name
      || item?.source_client_snapshot?.client_name
      || item?.client_name,
  );
  const parts = [orderNumber, sourceName].filter(Boolean);
  if (parts.length) return parts.join(" — ");
  return billableSourceLabel(item);
}

export function billableDestinationLabel(item) {
  const account = item?.billing_account || {};
  return clean(item?.billing_account_name || account.name || account.legal_name) || "Aucune entreprise associée";
}

export function billableBlockingReasons(item) {
  const reasons = item?.blocking_reasons || item?.issue_codes || [];
  const values = Array.isArray(reasons) ? reasons : [reasons];
  return values.map((reason) => {
    if (typeof reason === "object" && reason) {
      const code = clean(reason.code || reason.reason);
      return clean(reason.message) || BLOCKING_REASONS[code] || humanizeBillingValue(code);
    }
    const code = clean(reason);
    return BLOCKING_REASONS[code] || humanizeBillingValue(code);
  }).filter(Boolean);
}

function hasBillableBlockingReasons(item) {
  const reasons = item?.blocking_reasons || item?.issue_codes || [];
  const values = Array.isArray(reasons) ? reasons : [reasons];
  return values.some((reason) => {
    if (typeof reason === "object" && reason) {
      const code = clean(reason.code || reason.reason);
      const message = clean(reason.message) || BLOCKING_REASONS[code] || humanizeBillingValue(code);
      return Boolean(message);
    }
    const code = clean(reason);
    return Boolean(BLOCKING_REASONS[code] || humanizeBillingValue(code));
  });
}

export function isEligibleBillableItem(item) {
  if (!item?.id || hasBillableBlockingReasons(item)) return false;
  const status = normalized(item.eligibility_status || item.status);
  return !status || status === "eligible" || status === "ready";
}

export function selectableBillableItemIds(items) {
  return (items || []).filter(isEligibleBillableItem).map((item) => item.id);
}

export const BILLABLE_OVERDUE_DAYS_THRESHOLD = 7;

function startOfLocalDay(value = new Date()) {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
}

export function billableDeliveryDay(item) {
  const raw = clean(item?.delivery_date || item?.service_date);
  if (!raw) return null;
  const date = new Date(raw.length === 10 ? `${raw}T12:00:00` : raw);
  return startOfLocalDay(date);
}

export function billableDaysSinceDelivery(item, { today = new Date() } = {}) {
  const delivery = billableDeliveryDay(item);
  const comparison = startOfLocalDay(today);
  if (!delivery || !comparison) return null;
  const diffMs = comparison.getTime() - delivery.getTime();
  if (diffMs < 0) return 0;
  return Math.floor(diffMs / 86_400_000);
}

export function isBillableOverdue(item, { today = new Date(), threshold = BILLABLE_OVERDUE_DAYS_THRESHOLD } = {}) {
  const days = billableDaysSinceDelivery(item, { today });
  return days !== null && days >= threshold;
}

export function billableKpisFromSummary(summary = {}) {
  return {
    readyCount: Number(summary.ready_count) || 0,
    readyAmount: Number(summary.ready_amount) || 0,
    blockedCount: Number(summary.blocked_count) || 0,
    blockedAmount: summary.blocked_amount_unknown ? null : Number(summary.blocked_amount) || 0,
    approvedBlockedCount: Number(summary.approved_blocked_count) || 0,
    overdueCount: Number(summary.overdue_count) || 0,
    overdueReadyCount: Number(summary.overdue_ready_count) || 0,
    overdueBlockedCount: Number(summary.overdue_blocked_count) || 0,
    accountCount: Number(summary.account_count) || 0,
    blockedAccountCount: Number(summary.blocked_account_count) || 0,
    totalCount: Number(summary.total_count) || 0,
    blockingReasonCounts: billableBlockingReasonOptions(summary.blocking_reason_counts),
  };
}

export function billableBlockedReasonFilterOptions({
  reasonCounts = [],
} = {}) {
  return billableBlockingReasonOptions(reasonCounts);
}

export function filterBillableItemsByOverdue(items, { overdueOnly = false, today = new Date(), threshold = BILLABLE_OVERDUE_DAYS_THRESHOLD } = {}) {
  if (!overdueOnly) return Array.isArray(items) ? items : [];
  return (items || []).filter((item) => isBillableOverdue(item, { today, threshold }));
}

export function invoiceIssueBlockingReasons(invoice) {
  return Array.isArray(invoice?.issue_blocking_reasons)
    ? invoice.issue_blocking_reasons.filter((reason) => reason && typeof reason === "object")
    : [];
}

// A draft read without its Consolidation issue check: issuability is still unknown.
export function invoiceIssueChecksPending(invoice) {
  const lifecycle = normalized(invoice?.lifecycle_status || invoice?.status);
  return lifecycle === "draft" && invoice?.issue_checks_pending === true;
}

export function invoiceNeedsRevalidation(invoice) {
  const lifecycle = normalized(invoice?.lifecycle_status || invoice?.status);
  if (lifecycle !== "draft") return false;
  if (invoiceIssueChecksPending(invoice)) return false;
  if (invoice?.can_issue === true) return false;
  return invoice?.can_issue === false
    || normalized(invoice?.validation_status) === "blocked"
    || invoiceIssueBlockingReasons(invoice).length > 0;
}

export function isInvoiceIssueBlocked(invoice) {
  if (invoiceIssueChecksPending(invoice)) return false;
  if (invoiceNeedsRevalidation(invoice)) return true;
  if (invoice?.can_issue === true) return false;
  return Boolean(invoice?.is_stale || invoice?.stale);
}

export function invoiceDisplayStatus(invoice) {
  const lifecycle = normalized(invoice?.lifecycle_status || invoice?.status);
  if (lifecycle === "draft" && invoiceNeedsRevalidation(invoice)) return "revalidation";
  // Relevés seulement : jamais « en retard », le paiement se suit dans le logiciel de facturation.
  const serverStatus = normalized(invoice?.display_status);
  if (serverStatus) return serverStatus === "overdue" ? "sent" : serverStatus;
  const paymentStatus = normalized(invoice?.payment_status);
  if (lifecycle === "void") return "void";
  if (lifecycle === "paid" || paymentStatus === "paid") return "paid";
  if (lifecycle === "sent" || normalized(invoice?.send_status) === "sent") return "sent";
  if (lifecycle === "issued") return "issued";
  return lifecycle || "draft";
}

export function sanitizeBillingRateInput(value) {
  const normalized = clean(value)
    .replace(/\s*\$\/t\s*$/i, "")
    .replace(/\s*\$\s*$/i, "")
    .replace(",", ".");

  let result = "";
  let hasDecimal = false;

  for (const char of normalized) {
    if (char >= "0" && char <= "9") {
      result += char;
      continue;
    }
    if (char === "." && !hasDecimal) {
      hasDecimal = true;
      result += char;
    }
  }

  return result;
}

export function isValidBillingRate(value) {
  const normalized = sanitizeBillingRateInput(value);
  if (!normalized || normalized === ".") {
    return false;
  }

  const numeric = Number(normalized);
  return Number.isFinite(numeric) && numeric >= 0;
}

export const CREATE_BILLING_ACCOUNT_OPTION_VALUE = "__create_billing_account__";
export const CREATE_BILLING_ACCOUNT_OPTION_LABEL = "+ Nouvelle entreprise…";

export function isCreateBillingAccountOption(value) {
  return String(value ?? "") === CREATE_BILLING_ACCOUNT_OPTION_VALUE;
}

function billingRateValidationMessage(value, label) {
  const normalized = sanitizeBillingRateInput(value);
  if (!normalized || normalized === ".") {
    return `Le ${label} est requis.`;
  }
  if (!isValidBillingRate(normalized)) {
    return `Le ${label} doit être un nombre valide.`;
  }
  return "";
}

// Règle du navigateur pour un champ courriel (norme HTML) : celle du panneau « Envoyer le relevé ? », qui refuse sinon l'envoi.
const COMPLETE_EMAIL = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
export const INCOMPLETE_EMAIL_MESSAGE = "Saisissez une adresse complète, par exemple nom@exemple.com.";
export const isCompleteEmail = (value) => COMPLETE_EMAIL.test(clean(value));

export function resolveBillingAccountSaveDisabledMessages({
  draft = {},
  saving = false,
  busy = false,
} = {}) {
  if (saving) {
    return ["Enregistrement en cours."];
  }
  if (busy) {
    return ["Une action est en cours."];
  }

  const messages = [];
  if (!clean(draft.name)) {
    messages.push("Le nom de l’entreprise est requis.");
  }
  if (!clean(draft.billing_email)) {
    messages.push("Le courriel de facturation est requis.");
  } else if (!isCompleteEmail(draft.billing_email)) {
    messages.push(INCOMPLETE_EMAIL_MESSAGE);
  }
  if (!clean(draft.billing_address)) {
    messages.push("L’adresse de facturation est requise.");
  }
  if (!isValidBillingRate(draft.normal_hourly_rate)) {
    messages.push("Un tarif horaire valide est requis.");
  }
  for (const [kind, label] of [["tonne", "à la tonne"], ["voyage", "au voyage"]]) {
    if (draft[`${kind}_supplement_enabled`] && !isValidBillingRate(draft[`${kind}_supplement_rate`])) {
      messages.push(`Configurez un supplément ${label} valide ou désactivez-le.`);
    }
  }
  return messages;
}

export function issuerSettingsIssues(draft = {}) {
  const issues = {};
  const number = (value) => Number(clean(value).replace(",", "."));
  const decimals = (value) => (clean(value).replace(",", ".").split(".")[1] || "").length > 4;
  const rate = (field) => {
    const value = clean(draft[field]);
    if (!value) return;
    if (!Number.isFinite(number(value)) || number(value) < 0 || number(value) > 100) issues[field] = "Saisissez un taux entre 0 et 100.";
    else if (decimals(value)) issues[field] = "4 décimales au plus.";
  };
  const amount = (field, emptyMessage = "") => {
    const value = clean(draft[field]);
    if (!value) { if (emptyMessage) issues[field] = emptyMessage; return; }
    if (!Number.isFinite(number(value)) || number(value) < 0) issues[field] = "Saisissez un montant positif ou 0.";
    else if (decimals(value)) issues[field] = "4 décimales au plus.";
  };
  // Un champ vide ne changerait rien : l'ancien taux resterait appliqué.
  if (!clean(draft.fuel_surcharge_percent)) issues.fuel_surcharge_percent = "Saisissez 0 pour ne pas facturer de surtaxe.";
  else rate("fuel_surcharge_percent");
  rate("gst_rate_percent");
  rate("qst_rate_percent");
  amount("aspiration_fixed_rate", "Saisissez 0 pour ne pas facturer d’Aspiration.");
  amount("dompeur_tonne_rate");
  if (clean(draft.email) && !isCompleteEmail(draft.email)) issues.email = "Courriel invalide, par exemple « nom@exemple.com ».";
  return issues;
}

export function isBillingSetupIncomplete(issuer, accounts = []) {
  const legalName = clean(issuer?.legal_name);
  const address = clean(issuer?.address);
  const hasActiveAccount = (Array.isArray(accounts) ? accounts : []).some((account) => (
    account?.id && account.is_active !== false
  ));
  return !legalName || !address || !hasActiveAccount;
}

export function billableAccountBlockingCodes(items = []) {
  const codes = [];
  const seen = new Set();
  (Array.isArray(items) ? items : []).forEach((item) => {
    const reasons = item?.blocking_reasons || item?.issue_codes || [];
    const values = Array.isArray(reasons) ? reasons : [reasons];
    values.forEach((reason) => {
      const code = clean(typeof reason === "object" && reason ? (reason.code || reason.reason) : reason);
      // Camion dompeur sans $/t : son tarif se règle dans Paramètres, pas dans les entreprises.
      const key = code === "rate_missing" && item?.calculation_details?.kind === "dompeur" ? "dompeur_rate_missing" : code;
      if (!key || seen.has(key)) return;
      seen.add(key);
      codes.push(key);
    });
  });
  return codes;
}

export function billableBlockingFix(code) {
  const key = clean(code);
  if (["billing_mapping_required", "billing_account_missing"].includes(key)) {
    return { href: "/billing?tab=accounts", label: "Associer" };
  }
  if (key === "rate_missing") {
    return { href: "/billing?tab=accounts", label: "Ouvrir les entreprises" };
  }
  if (key === "dompeur_rate_missing") {
    return { href: "/billing?tab=billable&parametres=facturation", label: "Paramètres" };
  }
  return null;
}

/** Liste « Client » d'« À facturer » : les fiches « Entreprise facturée » (une ferme n'a jamais de prestation), plus celle déjà choisie. */
export function billableFilterClients(sourceClients = [], selectedId = "") {
  return (Array.isArray(sourceClients) ? sourceClients : []).filter((client) => client?.is_coop || (selectedId && client?.id === selectedId));
}

/** defaultSince : « Du » des filtres de départ d'« À facturer » (« Prêts »), quand ils sont seuls appliqués. */
export function billingEmptyState(view, { hasFilters = false, defaultSince = "" } = {}) {
  if (view === "invoices") return {
    title: hasFilters ? "Aucun relevé ne correspond aux filtres" : "Aucun relevé",
    message: hasFilters ? "Modifiez ou effacez les filtres." : "Les brouillons créés depuis « À facturer » apparaîtront ici.",
    canResetFilters: hasFilters,
  };
  if (defaultSince) return {
    title: `Aucune prestation prête depuis le ${dayWords(defaultSince)}`,
    message: "",
    canResetFilters: false,
    showAllStates: true,
  };
  return {
    title: hasFilters ? "Aucun élément ne correspond aux filtres" : "Rien à facturer",
    message: hasFilters ? "Modifiez ou effacez les filtres." : "Les prestations validées dans Consolidation apparaîtront ici.",
    canResetFilters: hasFilters,
  };
}
